import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import Autocomplete from "@/components/Autocomplete";
import ListItem from "@/components/ListItem/ListItem";
import { SOME_USERS_QUERY, type BackofficeUserOption } from "@/pages/people/queries";
import type { CoverageStaff } from "../types";

type StaffUserAutocompleteProps = {
  label: string;
  selectedUser: CoverageStaff | null;
  onSelect: (user: CoverageStaff | null) => void;
  disabled?: boolean;
  required?: boolean;
};

export default function StaffUserAutocomplete({
  label,
  selectedUser,
  onSelect,
  disabled = false,
  required = false
}: StaffUserAutocompleteProps) {
  const [searchTerm, setSearchTerm] = useState(selectedUser?.name ?? "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const autocompleteWrapperRef = useRef<HTMLDivElement>(null);

  const { data: usersData, loading: usersLoading } = useQuery(SOME_USERS_QUERY, {
    variables: { search: debouncedSearchTerm },
    skip: !debouncedSearchTerm
  });

  useEffect(() => {
    setSearchTerm(selectedUser?.name ?? "");
    if (selectedUser) {
      setDebouncedSearchTerm("");
    }
  }, [selectedUser]);

  useEffect(() => {
    if (selectedUser) {
      return;
    }

    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm, selectedUser]);

  const syncMenuWidth = useCallback(() => {
    const wrapper = autocompleteWrapperRef.current;
    if (!wrapper) return;

    const mdMenu = wrapper.querySelector("md-menu") as HTMLElement | null;
    const anchor = wrapper.querySelector(".relative");

    if (mdMenu && anchor) {
      const width = anchor.getBoundingClientRect().width;
      if (width > 0) {
        mdMenu.style.minWidth = `${width}px`;
        mdMenu.style.width = `${width}px`;
        mdMenu.style.maxWidth = `${width}px`;
      }
    }
  }, []);

  useEffect(() => {
    if (usersData) {
      requestAnimationFrame(syncMenuWidth);
    }
  }, [usersData, syncMenuWidth]);

  return (
    <div ref={autocompleteWrapperRef}>
      <Autocomplete<BackofficeUserOption>
        label={label}
        menuPositioning="fixed"
        required={required}
        disabled={disabled}
        query={selectedUser ? selectedUser.name : searchTerm}
        loadingResults={usersLoading}
        data={usersData?.backofficeUsers || []}
        onChangeText={(value) => {
          if (selectedUser) {
            onSelect(null);
          }
          setSearchTerm(value);
        }}
        reset={() => {
          onSelect(null);
          setSearchTerm("");
          setDebouncedSearchTerm("");
        }}
        onSelect={(user: BackofficeUserOption) => {
          onSelect({ id: user.id, name: user.name, email: user.email });
          setSearchTerm(user.name);
        }}
        renderItem={(user: BackofficeUserOption) => (
          <ListItem compact useAvatar iconName="person" headline={user.name} supportingText={user.email} />
        )}
      />
    </div>
  );
}
